<template>
	<header class="title-header">
		<section class="main-header">
			<div class="column--left" @click="onTitleClick">
				<input v-if="canEdit && isEditingTitle"
					:data-title-field="subDocId"
					ref="titleField"
					class="title-field"
					:value="title"
					@keydown="onTitleKeyDown"
					@blur="onTitleBlur"
				/>
				<h1 v-else class="title-text">
					<span>{{ title }}</span>
					<FontAwesomeIcon
						class="title-icon"
						:icon="['fas', 'pen-line']"
					/>
				</h1>
				<p class="date-text" @click="onDateClick">{{ shortDate }}</p>
				<a :href="virtualMeetingUrl" target="_blank" class="meeting-link-text" v-if="virtualMeetingUrl">
					{{ virtualMeetingUrl }}
				</a>
			</div>
			<div class="column--right">
				<div class="actions">
					<PopMenu
						:options="menuItems"
						@select="onMenuSelection"
					>
						<template #button="{ onClick, }">
							<TeamingButton2
								size="TINY"
								type="TERTIARY"
								@click="onClick"
							>
								<template #default>{{ translate( `actionsButton` ) }}</template>
								<template #right-icon>
									<FontAwesomeIcon :icon="[ `fas`, `caret-down` ]" />
								</template>
							</TeamingButton2>
						</template>
					</PopMenu>
				</div>
			</div>
		</section>
	</header>
</template>

<script>
import * as KeyCode from "keycode-js";

import {
	PopMenu,
	TranslateMixin,
	makePopMenuOption,
	TeamingButton2
} from "@teaming/common-ui";

export default {
	name: "TitleHeader",
	teamingPackage: "common-team",
	mixins: [ TranslateMixin, ],
	emits: [
		"assistMeeting",
		"scheduleRecurrence",
		"titleChange",
		"titleClick",
		"titleMenuActionSelected",
		"transcribe",
	],
	components: {
		PopMenu,
		TeamingButton2,
	},
	props: {
		documentName: {
			type: String,
			required: true,
		},
		subDocId: {
			type: String,
			required: true,
		},
		title: {
			type: String,
			required: true,
		},
		date: {
			type: Object,
			required: true,
		},
		canTranscribe: {
			type: Boolean,
			required: true,
		},
		canEdit: {
			type: Boolean,
			required: true,
		},
		canAddAssistant: {
			type: Boolean,
			required: true,
		},
		canRecur: {
			type: Boolean,
			required: true,
		},
		virtualMeetingUrl: {
			type: String,
			required: false,
		},
	},
	data() {
		return {
			isEditingTitle: false,
		};
	},
	computed: {
		shortDate() {
			const luxonDate = this.$luxonify( this.date );
			return luxonDate.toFormat( "MMM d, yyyy :: t" ); // Nov 15, 2023 :: 10:30am
		},
		menuItems() {
			const { translate, canTranscribe, canAddAssistant, canRecur, canEdit } = this;
			return [
				{
					text: translate( "editNoteNameMenuItem" ),
					value: "changeTitle",
					tooltip: canEdit ? "" : translate( "editDisabled" ),
					disabled: !canEdit,
				},
				{
					text: translate( "editDateAndTimeMenuItem" ),
					value: "changeDate",
					tooltip: canEdit? "" : translate( "editDisabled" ),
					disabled: !canEdit,
				},
				{
					text: translate( "saveAsTemplateMenuItem" ),
					value: "saveAsTemplate",
				},
				canAddAssistant && {
					text: translate( "addAssistantMenuItem" ),
					value: "addAssistant",
					icon: [ "fas", "message-bot", ],
				},
				canTranscribe && {
					text: translate( "transcribeMenuItem" ),
					value: "transcribe",
					icon: [ "fas", "message-bot", ],
				},
				{
					text: translate( "copyLinkToNoteMenuItem" ),
					value: "copyLink",
				},
				{
					text: translate( "deleteNoteMenuItem" ),
					value: "deleteNote",
					destructive: true,
				},
				canRecur && {
					text: translate( "scheduleRecurrenceMenuItem" ),
					value: "scheduleRecurrence",
				},
			].filter( o => !!o )
				.map( option => makePopMenuOption( option ) );
		},
	},
	methods: {
		focusTitle() {
			const { titleField, } = this.$refs;
			titleField.focus();
			titleField.setSelectionRange( 0, titleField.value.length );
		},
		emitChange() {
			const { titleField, } = this.$refs;
			if ( !titleField ) {
				return;
			}
			const { subDocId, } = this;
			const newTitle = titleField.value;
			this.$nextTick( () => {
				this.$emit( "titleChange", { subDocId, title: newTitle, } );
			} );
		},
		async onMenuSelection( { value, } ) {
			const { subDocId, canEdit } = this;
			let event = "titleMenuActionSelected";
			let payload = { action: value, subDocId, };

			if ( value === "changeTitle" && canEdit ) {
				this.isEditingTitle = true;
				await this.$nextTick();
				return this.focusTitle();
			}
			if ( value === "transcribe" ) {
				event = "transcribe";
				payload = { subDocId, };
			}
			if ( value === "assist" ) {
				event = "assistMeeting";
				payload = { subDocId, };
			}

			await this.$nextTick();
			this.$emit( event, payload );
		},
		onTitleClick( e ) {
			e.stopPropagation();
			if ( this.isEditingTitle ) {
				return;
			}
			this.isEditingTitle = true;
			this.$nextTick( () => {
				this.focusTitle();
			} );
		},
		onTitleKeyDown( e ) {
			if ( e.code === KeyCode.CODE_ESCAPE ) {
				this.isEditingTitle = false;
			}
			if ( [
				KeyCode.CODE_ENTER,
				KeyCode.CODE_RETURN,
				KeyCode.CODE_TAB,
			].includes( e.code ) ) {
				this.emitChange();
				this.isEditingTitle = false;
			}
		},
		onTitleBlur() {
			this.emitChange();
			this.isEditingTitle = false;
		},
		onDateClick( e ) {
			e.stopPropagation();
			if ( !this.canEdit ) {
				return;
			}
			this.$emit( "titleMenuActionSelected", { action: "changeDate", } );
		},
	},
	mounted() {
		const { subDocId, documentName, } = this;
		const manager = this.$documentRegistry()
			.manage( documentName );
		const latestSubdoc = manager.getLatestSubDoc();
		if ( latestSubdoc && latestSubdoc.subDocId === subDocId ) {
			this.isEditingTitle = true;
			this.$nextTick( () => {
				this.focusTitle();
			} );
		}
	},
};
</script>

<style lang="scss" scoped>
@mixin border-spacer {
	// invisible border that takes up exactly 1px below
	// an element; reserves the border space in the dom
	// so the title doesn't "move" when it acquires its
	// visible border, and the space between the date
	// and the title/title field is uniform
	border-bottom: 0.0625rem dotted transparent;
}

.title-header {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	gap: 0.5rem;
}

.title-header .main-header {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: flex-start;

	> div {
		display: flex;
		flex-direction: column;
		justify-content: flex-start;
		align-items: flex-start;
	}

	> div:first-child {
		flex: 1;
	}

	> div:nth-child(2) {
		align-items: flex-end;
	}
}

.title-header .title-text,
.title-header .title-field {
	@include font-and-line-for(1);
	font-weight: 600;
	color: $title-header-static-text-color;
}

.title-header .title-icon {
	@include font-and-line-for(1);
	font-weight: 600;
	color: $title-header-icon-color;
	display: none;
}

.title-header .title-text {
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 0.5rem;

	cursor: pointer;
	padding-bottom: 0;

	span {
		@include border-spacer;
	}
}

.title-header .title-text:hover {
	span {
		border-bottom: 0.0625rem dotted $title-header-border-color;
	}

	.title-icon {
		display: flex;
	}
}

.title-header .title-field {
	background-color: transparent;
	width: 100%;
	outline: none;
	border: none;
	border-radius: 0;

	padding-bottom: 0;
	@include border-spacer;
}

.title-header .date-text {
	@include font-and-line-for(-1);
	color: $title-header-date-text-color;
	cursor: pointer;
}

.title-header .meeting-link-text {
	@include font-and-line-for(-1);
	color: $default-hyperlink-font-color;
	cursor: pointer;
}

.title-header .actions {
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: center;
	gap: 0.5rem;
}
</style>

<style lang="scss">
.transcribe-button .text {
	@include for-size('xs') {
		display: none;
	}
}
</style>
